import { Settings2 } from '@tamagui/lucide-icons';
import { Accordion } from '@unistocks/components/accordion/accordion';
import { NativePressable } from '@unistocks/components/basic/pressable';
import { ScrollView } from '@unistocks/components/basic/scrollview';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Anchor } from '@unistocks/components/text/link';
import { useMappingHelper } from '@unistocks/components/virtual-list/list';
import { AssetImage } from '@unistocks/features/assets/components/asset-image';
import { useAssetSettings } from '@unistocks/features/portfolios/components/asset-settings/asset-settings-context';
import {
  getGenericColumn,
  validateSectionProperty
} from '@unistocks/features/portfolios/components/portfolio-table/utils';
import { useTableColumns } from '@unistocks/features/portfolios/hooks/use-table-columns';
import { isPortfolio as isPortfolioCheck } from '@unistocks/features/portfolios/portfolio-utils';
import { SectionAssetDto } from '@unistocks/proxy/axios-client';
import { triggerHaptics } from '@unistocks/shared/utils/haptics/haptics';
import { Link } from 'expo-router';
import React from 'react';
import { getVariableValue, isWeb, Paragraph, useMedia, useTheme, View } from 'tamagui';
import { TableCell, TableDetailsCell } from './table-cell';
import { TableRowProps } from './types';

export type PortfolioTableRowProps = TableRowProps & { index: number };

export function PortfolioTableRow({ portfolio, asset }: PortfolioTableRowProps) {
  const media = useMedia();
  const { getMappingKey } = useMappingHelper();
  const { openSettings } = useAssetSettings();
  const theme = useTheme();

  const { isReadonly } = portfolio;
  const isPortfolio = isPortfolioCheck(portfolio);
  const paddingY = getVariableValue('$1.5', 'space');
  const paddingX = getVariableValue('$2', 'space');
  const space3 = getVariableValue('$3', 'space');

  const { selected: selectedColumns, available: availableColumns } = useTableColumns(portfolio);
  const validatedAvailableColumns = availableColumns.filter(col =>
    validateSectionProperty([asset], col.prop)
  );

  const onRowLongPress = () => {
    triggerHaptics('longPress');
    openSettings(asset);
  };

  return (
    <Accordion collapsible style={{ backgroundColor: theme.background.get() }}>
      <Accordion.Item value="row">
        <Accordion.Trigger
          withFeedback={false}
          style={{
            paddingVertical: paddingY,
            paddingHorizontal: paddingX
          }}
          pressStyle={{ backgroundColor: theme.backgroundPress.get() }}
          hoverStyle={{ backgroundColor: theme.backgroundHover.get() }}
          onLongPress={isWeb ? undefined : onRowLongPress}
        >
          <Row h={55} gap={0}>
            <Row flexGrow={1} gap={'$3'} alignSelf="stretch">
              <AssetImage
                asset={asset}
                size={'$2.5'}
                display="none"
                $md={{ display: 'block', alignSelf: 'center' }}
              />

              <Column flexGrow={1}>
                <AssetName asset={asset} />

                <Row gap={0}>
                  <TableCell
                    column={nameCol}
                    asset={asset}
                    portfolio={isPortfolio ? portfolio : undefined}
                    textAlign="left"
                  />
                  {selectedColumns.map(column => (
                    <TableCell
                      key={column.prop}
                      column={column}
                      asset={asset}
                      portfolio={isPortfolio ? portfolio : undefined}
                    />
                  ))}
                </Row>
              </Column>
            </Row>
            {media.lg && !isReadonly ? (
              <NativePressable
                withFeedback
                withScale
                hitSlop={12}
                onPress={onRowLongPress}
                style={{
                  marginVertical: 'auto',
                  marginLeft: space3,
                  backgroundColor: 'transparent'
                }}
              >
                <Settings2 color={'$unistocks'} />
              </NativePressable>
            ) : null}
          </Row>
        </Accordion.Trigger>

        <Accordion.Content>
          <View my={'$2'}>
            <ScrollView horizontal showsHorizontalScrollIndicator={isWeb}>
              {validatedAvailableColumns.map((col, i) => (
                <TableDetailsCell
                  key={getMappingKey(col.prop, i)}
                  column={col}
                  asset={asset}
                  portfolio={isPortfolio ? portfolio : undefined}
                />
              ))}
            </ScrollView>
          </View>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion>
  );
}

PortfolioTableRow.displayName = 'PortfolioTableRow';

const nameCol = getGenericColumn('name');

const AssetName: React.FC<{ asset: SectionAssetDto }> = ({ asset }) => {
  return (
    <Row alignItems={'center'}>
      <AssetImage asset={asset} $md={{ display: 'none' }} />
      {asset.isListed ? (
        <Link
          asChild
          href={{
            pathname: '/(tabs)/asset/[assetId]',
            params: { assetId: asset.id }
          }}
          onPress={e => e.stopPropagation()}
        >
          <Anchor
            ellipsis
            numberOfLines={1}
            flexShrink={1}
            fs={'$2'}
            fw={'$7'}
            color={'$headingFont'}
            textDecorationLine="none"
            hoverStyle={{
              color: '$unistocks',
              textDecorationLine: 'underline'
            }}
            $md={{ fs: '$3' }}
            testID={`asset-details-${asset.id}`}
          >
            {asset.name}
          </Anchor>
        </Link>
      ) : (
        <Paragraph
          ellipsis
          numberOfLines={1}
          flexShrink={1}
          fs={'$2'}
          fw={'$7'}
          userSelect={'none'}
          $md={{ fs: '$3' }}
        >
          {asset.name}
        </Paragraph>
      )}
    </Row>
  );
};

AssetName.displayName = 'AssetName';
